import createApp from '@shopify/app-bridge';
import { createContext, useEffect, useState } from 'react';
import { SHOPIFY_API_KEY } from 'utils/configs';
import { setShopifyParams, getShopifyParams } from 'utils/shopifyStorage';
import {
  setAppBridgeInstance,
  getAppBridgeInstance,
} from 'services/axiosConfig';

export const ShopifyConfigContext = createContext({
  app: null,
  isShopifyEmbedded: false,
  host: null,
  shop: null,
});

const ShopifyProvider = ({ children }) => {
  const [contextValue, setContextValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlHost = params.get('host');
    const urlShop = params.get('shop');

    //** Fallback on sessionStorage */
    const storedParams = getShopifyParams();

    const host = urlHost || storedParams?.host;
    const shop = urlShop || storedParams?.shop;

    if (host || shop) {
      setShopifyParams(shop, host);
    }

    const isShopifyEmbedded = !!host;

    console.log('ShopifyProvider init:', { host, shop, isShopifyEmbedded });

    //** App Bridge - singleton */
    let app = getAppBridgeInstance();

    if (!app && isShopifyEmbedded && SHOPIFY_API_KEY && host) {
      try {
        app = createApp({
          apiKey: SHOPIFY_API_KEY,
          host,
          forceRedirect: true,
        });
        setAppBridgeInstance(app);
        console.log('✅ App Bridge created');
      } catch (error) {
        console.error('❌ App Bridge creation failed:', error);
        app = null;
      }
    } else if (app) {
      console.log('♻️ Using existing App Bridge');
    }

    return { host, shop, isShopifyEmbedded, app };
  });

  //** Listening for changes in URL parameters */
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const urlHost = params.get('host');
      const urlShop = params.get('shop');

      if (urlHost || urlShop) {
        const host = urlHost || contextValue.host;
        const shop = urlShop || contextValue.shop;

        setShopifyParams(shop, host);

        const isShopifyEmbedded = !!host;

        setContextValue(prev => ({
          ...prev,
          host,
          shop,
          isShopifyEmbedded,
          app: getAppBridgeInstance(),
        }));
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [contextValue.host, contextValue.shop]);

  return (
    <ShopifyConfigContext.Provider value={contextValue}>
      {children}
    </ShopifyConfigContext.Provider>
  );
};

export default ShopifyProvider;
